import React, { useState, useEffect } from 'react';
import { FiCheck, FiCreditCard, FiActivity, FiFileText, FiX, FiCopy, FiBarChart2, FiArrowRight, FiChevronDown } from 'react-icons/fi';
import { Link, useOutletContext } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import api from '../../api/axiosInstance';

const BillingManager = () => {
    const [monthlyCount, setMonthlyCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showNeftModal, setShowNeftModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [setBreadcrumb, setBreadcrumbLink] = useOutletContext();

    const chartData = {
        labels: historyData.map(d => d.month),
        datasets: [{
            label: 'Views',
            data: historyData.map(d => d.count),
            backgroundColor: 'rgba(57, 89, 255, 0.8)',
            borderRadius: 4,
        }]
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        setBreadcrumb(['Countdown', 'Billing']);
        setBreadcrumbLink(['/countdown']);
        fetchUsageData();
        fetchHistoryData(selectedYear);
    }, [selectedYear, setBreadcrumb, setBreadcrumbLink]);

    const fetchUsageData = async () => {
        try {
            const res = await api.get('/logs/count/monthly');
            setMonthlyCount(res.data.totalRequestsThisMonth);
        } catch (err) {
            console.error("Failed to fetch usage data:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistoryData = async (year) => {
        try {
            const res = await api.get(`/logs/stats/monthly-history?year=${year}`);
            setHistoryData(res.data);
        } catch (err) {
            console.error("Failed to fetch history data:", err);
        }
    };

    // Generate year options (last 3 years)
    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

    // Filter out future months
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-indexed
    const filteredHistory = historyData.filter(item => {
        // If it's a past year, show all months
        if (item.year < currentYear) return true;
        // If it's the current year, only show months up to current month
        if (item.year === currentYear) return item.monthNum <= currentMonth;
        // Don't show future years
        return false;
    });

    // Display logic: show most recent months, hide oldest ones
    // filteredHistory comes in Jan-Dec order, so reverse it to Dec-Jan
    const reversedHistory = [...filteredHistory].reverse();
    const displayedHistory = showAllHistory ? reversedHistory : reversedHistory.slice(0, 5);

    return (
        <>
            {showNeftModal && <NeftModal onClose={() => setShowNeftModal(false)} />}

            <div className="p-4 sm:p-6 bg-brand-background min-h-screen font-sans">
                {/* --- Header --- */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold text-brand-heading">Billing & Subscription</h1>
                    <p className="text-brand-paragraph mt-1">Manage your plan, payment methods, and view detailed usage reports.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* --- Left Column: Plan & Payment --- */}
                    <div className="space-y-6">

                        {/* --- Current Plan Card --- */}
                        <div className="bg-white p-6 rounded-lg border border-brand-border shadow-sm">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h2 className="text-lg font-bold text-brand-heading flex items-center gap-2">
                                        <FiCreditCard /> Current Plan
                                    </h2>
                                    <p className="text-sm text-brand-paragraph">You are currently on the <span className="font-semibold text-brand-heading">Email Countdown Enterprise</span>.</p>
                                </div>
                            </div>

                            <div className="border-t border-gray-100 pt-6">
                                <div className="mb-6">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-4xl font-bold text-brand-heading">Custom</span>
                                    </div>
                                </div>
                                <div className="mb-6">
                                    <ul className="space-y-3">
                                        <li className="flex items-center gap-2 text-sm text-brand-paragraph">
                                            <FiCheck className="text-brand-success flex-shrink-0" /> Unlimited Timers
                                        </li>
                                        <li className="flex items-center gap-2 text-sm text-brand-paragraph">
                                            <FiCheck className="text-brand-success flex-shrink-0" /> Unlimited views/mo
                                        </li>
                                        <li className="flex items-center gap-2 text-sm text-brand-paragraph">
                                            <FiCheck className="text-brand-success flex-shrink-0" /> Full Customization
                                        </li>
                                        <li className="flex items-center gap-2 text-sm text-brand-paragraph">
                                            <FiCheck className="text-brand-success flex-shrink-0" /> Easy Integration
                                        </li>
                                    </ul>
                                </div>
                                <Link target='_blank' to="https://webhizzy.in/contact.html#contact-form">
                                    <button className="w-full py-2 px-6 text-sm font-medium text-white bg-brand-accent rounded-lg hover:bg-blue-700 transition-colors opacity-80">
                                        Feedback
                                    </button>
                                </Link>
                            </div>
                        </div>

                        {/* --- Payment Method --- */}
                        <div className="bg-white p-6 rounded-lg border border-brand-border shadow-sm">
                            <h2 className="text-lg font-bold text-brand-heading flex items-center gap-2 mb-4">
                                <FiCreditCard /> Payment Method
                            </h2>
                            <div className="p-4 border border-brand-border rounded-lg bg-gray-50">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-bold text-brand-heading">Manual / NEFT</span>
                                    <span className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">Default</span>
                                </div>
                                <p className="text-xs text-brand-paragraph mb-3">
                                    Direct bank transfer.
                                </p>
                                <button
                                    onClick={() => setShowNeftModal(true)}
                                    className="text-xs font-medium text-brand-accent hover:text-blue-700 underline"
                                >
                                    View Instructions
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* --- Right Column: Usage Viewer --- */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* --- Usage Overview --- */}
                        <div className="bg-white p-6 rounded-lg border border-brand-border shadow-sm">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-lg font-bold text-brand-heading flex items-center gap-2">
                                    <FiActivity /> Usage Overview
                                </h2>
                                <div className="text-right">
                                    <p className="text-sm text-brand-paragraph">Current Month</p>
                                    <p className="text-2xl font-bold text-brand-heading">
                                        {loading ? '...' : monthlyCount.toLocaleString()}
                                    </p>
                                </div>
                            </div>

                            {/* Monthly Chart */}
                            <div className="h-64 mb-6">
                                <Bar
                                    data={chartData}
                                    options={{
                                        maintainAspectRatio: false,
                                        plugins: { legend: { display: false } },
                                        scales: { y: { beginAtZero: true } }
                                    }}
                                />
                            </div>
                        </div>

                        {/* --- Usage History List --- */}
                        <div className="bg-white p-6 rounded-lg border border-brand-border shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-bold text-brand-heading flex items-center gap-2">
                                    <FiFileText /> Usage History
                                </h2>
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                                    className="px-3 py-1.5 text-sm border border-brand-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-accent"
                                >
                                    {yearOptions.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-1">
                                {/* Render history newest first */}
                                {displayedHistory.map((item, i) => (
                                    <div key={i} className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors border-b border-gray-50 last:border-0">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-brand-accent-light text-brand-accent rounded-lg">
                                                <FiBarChart2 size={20} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-brand-heading">{item.fullMonth} {item.year}</p>
                                                <p className="text-xs text-gray-500">Monthly Report</p>
                                            </div>
                                        </div>
                                        <Link
                                            to={`/countdown/billing/report/${item.year}/${item.monthNum}`}
                                            className="flex items-center gap-2 text-sm font-medium text-brand-accent hover:text-blue-700 bg-white border border-brand-border px-3 py-1.5 rounded-md hover:bg-gray-50 transition-colors"
                                        >
                                            View Report <FiArrowRight />
                                        </Link>
                                    </div>
                                ))}
                            </div>

                            {/* View All Button */}
                            {filteredHistory.length > 5 && (
                                <div className="mt-4 text-center">
                                    <button
                                        onClick={() => setShowAllHistory(!showAllHistory)}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand-accent hover:text-blue-700 bg-gray-50 border border-brand-border rounded-md hover:bg-gray-100 transition-all"
                                    >
                                        {showAllHistory ? 'Show Less' : `View All (${filteredHistory.length - 5} more)`}
                                        <FiChevronDown className={`transform transition-transform ${showAllHistory ? 'rotate-180' : ''}`} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const NeftModal = ({ onClose }) => {
    const [copiedField, setCopiedField] = useState(null);

    const details = [
        { label: 'Bank Name', value: 'State Bank of India' },
        { label: 'Account Name', value: 'WEBHIZZY SOLUTIONS' },
        { label: 'Account Number', value: '43342072422' },
        { label: 'IFSC Code', value: 'SBIN0050836' },
    ];

    const handleCopy = (value, label) => {
        navigator.clipboard.writeText(value);
        setCopiedField(label);
        setTimeout(() => setCopiedField(null), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4 transition-opacity">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md transform transition-all scale-100">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-brand-heading">NEFT Payment Details</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><FiX size={24} /></button>
                </div>

                <div className="space-y-4">
                    {details.map((item) => (
                        <div key={item.label} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div>
                                <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                                <p className="text-sm font-mono font-medium text-brand-heading">{item.value}</p>
                            </div>
                            <button
                                onClick={() => handleCopy(item.value, item.label)}
                                className={`p-2 rounded-md transition-colors ${copiedField === item.label ? 'text-green-600 bg-green-100' : 'text-brand-accent hover:bg-blue-100'}`}
                                title="Copy"
                            >
                                {copiedField === item.label ? <FiCheck size={18} /> : <FiCopy size={18} />}
                            </button>
                        </div>
                    ))}
                </div>

                <div className="mt-6 text-center">
                    <p className="text-xs text-gray-500">
                        Please mention your account email in the transaction remarks.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default BillingManager;
